import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";

import { FormGroup } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";

import { gql } from "__generated__/gql";
import { ContactRoleType } from "__generated__/graphql";

import { formatCharterForMutation } from "../dossier/utils";
import { SalesCharter, SalesCharterContact } from "types/index";
import { Contact } from "types/commons";
import ContactSelect from "view/relations/components/selects/ContactSelect";

type UpdateCharterContactsDialogProps = {
	contactRoles: ContactRoleType[];
	open: boolean;
	onClose: () => void;
	charter: SalesCharter;
	refetch: () => void;
};

type CharterContactsForm = {
	[key in ContactRoleType]?: Contact[] | null;
};

const UpdateCharterContactsDialog = ({ contactRoles, open, onClose, charter, refetch }: UpdateCharterContactsDialogProps) => {
	const defaultValues = Object.fromEntries(contactRoles.map(t => [ t, null ]));

	const { control, handleSubmit, reset, setValue, getValues } = useForm<CharterContactsForm>({
		defaultValues: defaultValues,
	});

	const [ saveCharter, { loading } ] = useMutation(mutation);

	const handleOnClose = () => {
		onClose();
		reset();
	};

	const save = (data: CharterContactsForm) => {

		const charterContacts = [] as SalesCharterContact[];

		Object.entries(data).forEach(([ role, contacts ]) => {
			if (contacts) {
				contacts.forEach(contact => {
					charterContacts.push({
						contactRoleType: role as ContactRoleType,
						contact: contact,
					});
				});
			}
		});

		const input = formatCharterForMutation({
			id: charter.id,
			charterNumber: charter.charterNumber,
			type: charter.type,
			subtype: charter.subtype,
			creditorId: charter.creditor.id,
			debtorId: charter.debtor.id,
			brokerId: charter.broker?.id || 0,
			relationType: charter.relationType,
			contacts: charterContacts,
			minimumShipNominationDays: charter.minimumShipNominationDays || null,
			shipNominationPeriodRegime: charter.shipNominationPeriodRegime || null,
			shortNotice: charter.shortNotice || false,
			customerNotes: charter.customerNotes || "",
			internalNotes: charter.internalNotes || "",
			customerReference: charter.customerReference || "",
			blDate: charter.blDate || 0,
		});

		saveCharter({
			variables: {
				input: input,
			},
		}).then(() => {
			onClose();
			refetch();
		});
	};

	useEffect(() => {
		if (open) {
			reset([ ...charter.contacts || [] ].reduce<{ [key in ContactRoleType]?: Contact[] }>((acc, { contactRoleType, contact }) => {
				if (!acc[contactRoleType]) {
					acc[contactRoleType] = [];
				}
				acc[contactRoleType].push(contact as Contact);
				return acc;
			}, defaultValues));
		}
	}, [ open ]);

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "CONTACTS" })}
			form='updateCharterContactsForm'
			loading={loading}>
			<React.Fragment>
				<form onSubmit={(event) => void handleSubmit(save)(event)} id="updateCharterContactsForm">
					{contactRoles.map((role, index) => (
						<FormGroup
							label={intl.formatMessage({ id: role })}
							labelPlacement="left"
							labelPaddingTop={14}
							key={index}
							overflow="visible">
							<Controller
								name={role}
								control={control}
								render={({ field }) => (
									<ContactSelect
										{...field}
										onChange={option => {
											const selectedContacts = option && option.length > 0 ? option : null;
											field.onChange(selectedContacts);

											if (role !== ContactRoleType.OrderEntry || selectedContacts == null) {
												return;
											}

											selectedContacts.forEach(x => {
												const roles = x.contactRoleTypes ?? [];

												roles.forEach(y => {
													if (y === ContactRoleType.OrderEntry) {
														return;
													}

													if (!contactRoles.includes(y)) {
														return;
													}

													const currentContacts = getValues(y) ?? [];
													const newContacts = currentContacts
														? (currentContacts.some(z => z.id === x.id) ? currentContacts : [ ...currentContacts, x ])
														: [ x ];

													setValue(y, newContacts.length > 0 ? newContacts : []);
												});
											});

										}}
										roleType={role}
										relations={charter.broker ? [ charter.broker, charter.debtor ] : [ charter.debtor ]} />
								)} />
						</FormGroup>
					))}
				</form>
			</React.Fragment>
		</FormDialog>
	);
};

const mutation = gql(`#graphql
	mutation saveCharter_updateCharterContactsDialog($input: CharterInput!) {
		saveCharter(input: $input) {
			id
			type
		}
	}
`);

export default UpdateCharterContactsDialog;
